const originalConsole = window.console;
let internalLogEventFunction = () => {};
let internalLogLevel;

//this corresponds to the configuration VUE_APP_CONSOLE_LOG_EVENT_LEVEL
export const logLevels = {
	debug: 'debug',
	warn: 'warn',
	error: 'error'
};

//this corresponds to the EventLevels enum of the log events API
export const eventLevels = {
	info: 'info',
	warn: 'warn',
	error: 'error'
};

export const restoreConsole = () => {
	Object.defineProperty(window, 'console', {
		writable: true,
		value: originalConsole
	});
};

//the proxy handler object
export const handler = {
	get(target, key) {
		//intercepts any calls to get the console.log, console.warn, and console.error functions
		//and returns the proxy function
		switch (key) {
			case 'info':
			case 'log':
			case 'warn':
			case 'error':
				return proxyFunc(target, key);
			default:
				return Reflect.get(...arguments);
		}
	}
};

//the function the console proxy will call info, log, warn, or error are called
export const proxyFunc = (target, key) => (...args) => {
	//This function calls our logging function and then calls the original console function
	//with the same arguments
	let eventLevel = eventLevels.info;
	const trace = new Error().stack;

	switch (key) {
		case 'warn':
			eventLevel = eventLevels.warn;
			break;
		case 'error':
			eventLevel = eventLevels.error;
			break;
		default:
			eventLevel = eventLevels.info;
			break;
	}

	//if logLevel is error, only log errors
	if (internalLogLevel === logLevels.error && key === 'error') {
		internalLogEventFunction(
			key,
			eventLevel,
			'console',
			gatherArgs(args, key, trace)
		);
	}
	//if logLevel is warn, log errors and warnings
	else if (
		internalLogLevel === logLevels.warn &&
		(key === 'warn' || key === 'error')
	) {
		internalLogEventFunction(
			key,
			eventLevel,
			'console',
			gatherArgs(args, key, trace)
		);
	}
	//if logLevel is debug, log everything
	else if (internalLogLevel === logLevels.debug) {
		internalLogEventFunction(
			key,
			eventLevel,
			'console',
			gatherArgs(args, key, trace)
		);
	}

	target[key](...args);
};

export const gatherArgs = (args, funcName, trace) => {
	const eventObj = {
		message: undefined,
		stack: trace,
		type: `console.${funcName}`,
		args: []
	};

	if (!Array.isArray(args)) {
		return args;
	}

	args.forEach(arg => {
		if (arg instanceof Error) {
			//if an error was passed in, extract the message and stack properties
			if (eventObj.message === undefined || eventObj.message === null) {
				eventObj.message = arg.message;
			} else {
				args.push(arg.message);
			}

			if (arg.stack) {
				eventObj.stack = arg.stack;
			}

			eventObj.args.push(parseError(arg));
		} else if (typeof arg === 'string' && !eventObj.message) {
			//put the first string arg into the mesage if it's empty
			eventObj.message = arg;
		} else {
			//put all other args into the args array
			eventObj.args.push(arg);
		}
	});

	return eventObj;
};

export const parseError = error => {
	const data = {};

	if (error) {
		if (error.name === 'AxiosError') {
			Object.assign(data, parseAxiosError(error));
		}
	}

	return data;
};

//function called on window error and unhandledrejection events
export const errorHandler = event => {
	let eventObj;

	//this function accounts for either ErrorEvent or PromiseRejectionEvent
	if (event) {
		eventObj = event;

		if (event instanceof ErrorEvent) {
			eventObj = {
				message: event.message,
				stack: event.error.stack,
				type: event.type
			};
		} else if (event instanceof PromiseRejectionEvent) {
			eventObj = parseReason(event);
		}

		internalLogEventFunction('error', logLevels.error, 'window', eventObj);
	}
};

export const parseReason = event => {
	const reason = {};

	if (event && event.reason) {
		reason.message = event.reason.message;
		reason.stack = event.reason.stack;
		reason.type = event.type;

		if (event.reason.name === 'AxiosError') {
			Object.assign(reason, parseAxiosReason(event));
		}
	}

	return reason;
};

export const parseAxiosReason = event => {
	const reason = {
		name: event?.reason?.name
	};

	//extract relevant data from the axios failure reason
	if (event.reason.response) {
		Object.assign(reason, parseAxiosResponse(event.reason.response));
		// reason.status = event.reason.response.status;
		// reason.statusText = event.reason.response.statusText;

		// if (event.reason.response.config) {
		// 	reason.url = event.reason.response.config.url;
		// 	reason.baseUrl = event.reason.response.config.baseURL;
		// 	reason.headers = event.reason.response.config.headers;
		// 	reason.method = event.reason.response.config.method;
		// }
	}

	return reason;
};

export const parseAxiosError = error => {
	const reason = {};

	if (error) {
		reason.code = error.code;
		reason.message = error.message;
		reason.name = error.name;

		if (error.response) {
			Object.assign(reason, parseAxiosResponse(error.response));
		}
	}

	return reason;
};

export const parseAxiosResponse = response => {
	const reason = {};

	//extract relevant data from the axios failure reason
	if (response) {
		reason.status = response.status;
		reason.statusText = response.statusText;

		if (response.config) {
			reason.url = response.config.url;
			reason.baseUrl = response.config.baseURL;
			reason.headers = response.config.headers;
			reason.method = response.config.method;
		}
	}

	return reason;
};

//function to set up the console proxy and error handling to call the log api
export const useConsoleEventLogging = (logLevel, logEventFunction) => {
	internalLogLevel = logLevel;

	if (
		internalLogLevel &&
		Object.prototype.hasOwnProperty.call(logLevels, internalLogLevel)
	) {
		internalLogEventFunction = logEventFunction;

		//listen for synchronous errors so we can log them
		window.removeEventListener('error', errorHandler);
		window.addEventListener('error', errorHandler);

		//listen for asynchronous errors so we can log them
		window.removeEventListener('unhandledrejection', errorHandler);
		window.addEventListener('unhandledrejection', errorHandler);

		//create a proxy object for the console object
		const proxy = new Proxy(window.console, handler);

		//replace the window.console object with the proxy object
		Object.defineProperty(window, 'console', {
			writable: true,
			value: proxy
		});
	}
};
